import { useState } from 'react';

type DialogMode = 'closed' | 'menu' | 'dm' | 'group';

interface NewChatDialogProps {
  onCreateDM: (target: string) => Promise<void>;
  onCreateGroup: (name: string, description: string) => Promise<void>;
}

export function NewChatDialog({ onCreateDM, onCreateGroup }: NewChatDialogProps) {
  const [mode, setMode] = useState<DialogMode>('closed');
  const [recipient, setRecipient] = useState('');
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenMenu = () => {
    setMode('menu');
    setError(null);
  };

  const handleClose = () => {
    setMode('closed');
    setRecipient('');
    setGroupName('');
    setGroupDescription('');
    setError(null);
  };

  const handleSubmitDM = async () => {
    const trimmed = recipient.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    try {
      await onCreateDM(trimmed);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open conversation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitGroup = async () => {
    const name = groupName.trim();
    if (!name) return;
    setSubmitting(true);
    setError(null);
    try {
      await onCreateGroup(name, groupDescription.trim());
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create group');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, onSubmit: () => void) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleOpenMenu}
        className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
        title="New conversation"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 5v14" />
          <path d="M5 12h14" />
        </svg>
      </button>

      {mode !== 'closed' && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={handleClose} />

          {/* Dropdown */}
          <div className="absolute left-0 top-full mt-1 z-50 w-64 bg-card border border-border rounded-lg shadow-lg">
            {mode === 'menu' && (
              <div className="p-1">
                <button
                  onClick={() => setMode('dm')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm rounded-md hover:bg-muted transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-muted-foreground"
                  >
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <div>
                    <div className="font-medium text-foreground">New Message</div>
                    <div className="text-xs text-muted-foreground">Start a direct conversation</div>
                  </div>
                </button>
                <button
                  onClick={() => setMode('group')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm rounded-md hover:bg-muted transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-muted-foreground"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  <div>
                    <div className="font-medium text-foreground">New Group</div>
                    <div className="text-xs text-muted-foreground">Create a group channel</div>
                  </div>
                </button>
              </div>
            )}

            {mode === 'dm' && (
              <div className="p-3">
                <div className="text-sm font-medium text-foreground mb-2">New Message</div>
                <input
                  type="text"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleSubmitDM)}
                  placeholder="Username or email..."
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                  autoFocus
                  disabled={submitting}
                />
                {error && <p className="text-xs text-destructive mt-1.5">{error}</p>}
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    onClick={handleClose}
                    className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitDM}
                    disabled={!recipient.trim() || submitting}
                    className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  >
                    {submitting ? 'Opening...' : 'Open'}
                  </button>
                </div>
              </div>
            )}

            {mode === 'group' && (
              <div className="p-3">
                <div className="text-sm font-medium text-foreground mb-2">New Group</div>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleSubmitGroup)}
                  placeholder="Group name..."
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 mb-2"
                  autoFocus
                  disabled={submitting}
                />
                <input
                  type="text"
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleSubmitGroup)}
                  placeholder="Description (optional)..."
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                  disabled={submitting}
                />
                {error && <p className="text-xs text-destructive mt-1.5">{error}</p>}
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    onClick={handleClose}
                    className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitGroup}
                    disabled={!groupName.trim() || submitting}
                    className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  >
                    {submitting ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
